import React, { useState, useEffect, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Button,
	Badge,
	Progress,
} from "@/components/primitives";
import {
	Brain,
	TrendingUp,
	FileText,
	ChevronUp,
	ChevronDown,
	BarChart3,
	Target,
} from "lucide-react";
import {
	LineChart,
	Line,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
	BarChart,
	Bar,
	PieChart,
	Pie,
	Cell,
} from "recharts";

interface Group {
	id: string;
	name: string;
	description: string;
	member_count: number;
	members: GroupMember[];
}

interface GroupMember {
	id: string;
	mentee_id: string;
	mentee_name: string;
	mentee_email: string;
	created_at: string;
}

interface AIGroupInsights {
	// NEW: Reflection Report Analysis (Deep, Point-in-Time)
	reflectionReportAnalysis?: {
		cognitiveFrameworks?: {
			dominantMentalModels?: Array<{
				model: string;
				prevalence: string;
				examples: string[];
			}>;
			cognitiveBiases?: Array<{
				bias: string;
				impact: "positive" | "negative" | "neutral";
				examples: string[];
			}>;
		};
		developmentalInsights?: {
			maturityDistribution?: { high: number; medium: number; low: number };
			growthIndicators?: string[];
			readinessLevels?: {
				ready: number;
				developing: number;
				not_ready: number;
			};
			coachingOpportunities?: Array<{
				opportunity: string;
				targetMembers: string[];
				approach: string;
			}>;
		};
		topicSpecificInsights?: {
			howTopicManifests?: string;
			unknownAspectsRevealed?: string[];
			deeperUnderstanding?: string;
		};
	};
	// NEW: Reflection Check-in Analysis (Temporal, Situational)
	reflectionCheckInAnalysis?: {
		applicationTracking?: {
			actionFollowThrough?: {
				planned: number | string;
				taken: number | string;
				gap: string;
			};
			realTimeAwareness?: { high: number; medium: number; low: number };
		};
		temporalPatterns?: {
			triggerPatterns?: Array<{
				trigger: string;
				frequency: string;
				impact: "positive" | "negative" | "mixed";
			}>;
			stressCycles?: {
				pattern: string;
				frequency: "daily" | "weekly" | "monthly";
				peakTimes: string[];
			};
			moodCorrelations?: Array<{
				correlation: string;
				strength: "strong" | "moderate" | "weak";
			}>;
			cyclicalBehaviors?: string[];
		};
		situationalContext?: {
			contextSpecificManifestations?: Array<{
				context: string;
				howTopicShowsUp: string;
			}>;
			environmentalTriggers?: string[];
			socialDynamics?: Array<{
				dynamic: string;
				impact: "positive" | "negative" | "neutral";
			}>;
			immediateVsLongTerm?: {
				immediateReactions: string;
				sustainedChange: string;
			};
		};
		realTimeInsights?: {
			whatsWorkingNow?: string[];
			whatsNotWorking?: string[];
			emergingPatterns?: string[];
			interventionEffectiveness?: {
				effective: string[];
				ineffective: string[];
				recommendations: string[];
			};
		};
	};
	// NEW: Integrated Synthesis (Combining Both)
	integratedSynthesis?: {
		insightToActionGap?: {
			barriersToApplication?: string[];
			supportNeeds?: string[];
			successFactors?: string[];
		};
		topicManifestationMap?: {
			howTopicShowsUp?: {
				inReports: string;
				inCheckIns: string;
				differences: string;
			};
			unknownAspects?: string[];
			hiddenPatterns?: string[];
			realityVsPerception?: string;
		};
	};
	// LEGACY: Original analysis structure (for backward compatibility)
	executiveSummary?: {
		overallHealth: "excellent" | "good" | "concerning" | "critical";
		keyFindings: string[];
		strategicRecommendations: string[];
		timeframePriority: "immediate" | "short-term" | "medium-term" | "long-term";
	};
	sentimentAnalysis: {
		overallTrajectory:
			| "strongly_improving"
			| "improving"
			| "stable"
			| "declining"
			| "rapidly_declining";
		volatilityPattern: "high" | "medium" | "low";
		emotionalUndercurrents: string[];
		moodDrivers: Array<{
			factor: string;
			impact: "positive" | "negative" | "mixed";
			frequency: string;
			examples: string[];
			intervention: string;
		}>;
		chartData: {
			sentimentTrend: Array<{
				date: string;
				averageSentiment: number;
				memberName: string;
				keyEvent?: string;
			}>;
		};
	};
	thematicAnalysis: {
		emergingNarratives: Array<{
			theme: string;
			prevalence: string;
			evolution: string;
			psychologicalSignificance: string;
			linguisticMarkers: string[];
			interventionOpportunities: string[];
		}>;
		cognitivePatterns: Array<{
			pattern: string;
			frequency: string;
			impact: "positive" | "negative" | "neutral";
			examples: string[];
			developmentImplications: string;
		}>;
		communicationStyles: {
			groupCommunicationHealth:
				| "excellent"
				| "good"
				| "needs_improvement"
				| "poor";
			vulnerabilityLevel: "high" | "medium" | "low";
			supportBehaviors: string[];
			challengingDynamics: string[];
		};
	};
	individualAnalysis: Array<{
		memberName: string;
		developmentalStage:
			| "breakthrough"
			| "growth"
			| "plateau"
			| "struggle"
			| "crisis";
		engagementLevel: "high" | "medium" | "low";
		riskFactors: string[];
		strengths: string[];
		uniqueContribution: string;
		growthOpportunities: string[];
		recommendedInterventions: string[];
		predictiveInsights: string;
	}>;
	systemicInsights: {
		groupDynamicsAssessment:
			| "cohesive"
			| "forming"
			| "fragmenting"
			| "dysfunctional";
		leadershipEmergence: string[];
		conflictPatterns: string[];
		supportSystems: string[];
		culturalFactors: string[];
		systemicIssues: Array<{
			issue: string;
			rootCause: string;
			manifestations: string[];
			systemicSolution: string;
		}>;
	};
	actionableRecommendations: Array<{
		priority: "critical" | "high" | "medium" | "low";
		category:
			| "individual_intervention"
			| "group_intervention"
			| "systemic_change"
			| "strategic_direction";
		title: string;
		description: string;
		rationale: string;
		expectedOutcome: string;
		implementation: {
			timeline: string;
			resources: string[];
			successMetrics: string[];
			riskMitigation: string[];
		};
		targetMembers?: string[];
		leveragePoints: string[];
	}>;
	futureStateVisioning: {
		currentTrajectory: string;
		optimalOutcomes: string[];
		pathwayToSuccess: string;
		keyMilestones: string[];
		potentialObstacles: string[];
		successIndicators: string[];
	};
	chartDataRecommendations?: Array<{
		chartType: "line" | "bar" | "scatter" | "heatmap" | "network";
		title: string;
		description: string;
		dataStructure: string;
		insights: string;
	}>;
}

interface GroupInsights {
	groupId: string;
	groupName: string;
	generatedAt: string;
	aiAnalysis: AIGroupInsights;
	metadata: {
		reflectionReports: number;
		reflectionCheckIns: number;
		generalCheckIns?: number;
		members: number;
		sectionsCompleted?: number;
		sectionsTotal?: number;
		failedSections?: string[];
	};
}

interface GroupAnalysisSectionProps {
	group: Group;
	topicId?: string; // Optional topic ID for topic-specific analysis
}

export function GroupAnalysisSection({
	group,
	topicId,
}: GroupAnalysisSectionProps) {
	const { user } = useAuth();
	const [groupInsights, setGroupInsights] = useState<GroupInsights | null>(
		null,
	);
	const [generatingInsights, setGeneratingInsights] = useState(false);
	const [showInsights, setShowInsights] = useState(false);

	// Track last loaded group key to avoid redundant loads/reprocessing
	const lastLoadedKeyRef = useRef<string | null>(null);

	useEffect(() => {
		if (group?.id) {
			const groupKey = topicId ? `topic-${topicId}` : group.id;
			// Skip if we've already loaded this key
			if (lastLoadedKeyRef.current === groupKey) return;
			loadExistingGroupAnalysis();
		}
	}, [group?.id, user?.id, topicId]);

	// Load existing group analysis from database
	const loadExistingGroupAnalysis = async () => {
		if (!user?.id || !group?.id) return;

		const groupKey = topicId ? `topic-${topicId}` : group.id;

		try {
			// Prevent duplicate concurrent loads
			if (lastLoadedKeyRef.current === groupKey) return;

			console.log(`🔍 Loading group analysis for group key: ${groupKey}`);

			let analysisData: any[] | null = null;
			let error: any = null;

			try {
				if (topicId) {
					// Load topic-specific analysis using topic_id
					const resp = await supabase
						.from("group_analysis")
						.select("*")
						.eq("mentor_id", user.id)
						.eq("topic_id", topicId)
						.order("created_at", { ascending: false })
						.limit(1);

					analysisData = (resp as any).data;
					error = (resp as any).error;
				} else {
					// Load group-specific analysis using group_id
					const resp = await supabase
						.from("group_analysis")
						.select("*")
						.eq("mentor_id", user.id)
						.eq("group_id", group.id)
						.order("created_at", { ascending: false })
						.limit(1);

					analysisData = (resp as any).data;
					error = (resp as any).error;
				}
			} catch (e) {
				// Catch low-level client errors (e.g. bad request due to type mismatch)
				console.warn(
					`⚠️ Supabase query failed for group_key=${groupKey}, will attempt fallback.`,
					e,
				);
				analysisData = null;
				error = e;
			}

			// If no topic-specific analysis found and we have a topic, try to find general group analysis
			if (
				(!analysisData || analysisData.length === 0) &&
				topicId &&
				group.members.length > 0
			) {
				console.log(
					`🔍 No topic-specific analysis found, checking for general group analysis`,
				);

				// Try to find recent general analysis for this mentor
				try {
					const resp = await supabase
						.from("group_analysis")
						.select("*")
						.eq("mentor_id", user.id)
						.order("created_at", { ascending: false })
						.limit(1);

					const generalAnalysis = (resp as any).data;
					const generalError = (resp as any).error;

					if (generalAnalysis && generalAnalysis.length > 0) {
						// Use the most recent general analysis
						analysisData = [generalAnalysis[0]];
						console.log(
							`🔍 Using general group analysis: ${generalAnalysis[0].group_name}`,
						);
					}

					if (generalError) {
						console.warn("⚠️ Error fetching general analysis:", generalError);
					}
				} catch (e) {
					console.warn("⚠️ General analysis query failed:", e);
				}
			}

			if (!analysisData || analysisData.length === 0) {
				// mark as attempted so we don't repeatedly re-query on every render
				lastLoadedKeyRef.current = groupKey;
				return;
			}

			const analysis = analysisData[0];
			const insights: GroupInsights = {
				groupId: analysis.group_id || `topic-${analysis.topic_id}`,
				groupName: analysis.group_name,
				generatedAt: analysis.created_at,
				aiAnalysis: analysis.analysis_data,
				metadata: {
					reflectionReports: analysis.reflection_count || 0,
					reflectionCheckIns: analysis.checkin_count || 0,
					generalCheckIns: 0, // Not stored separately, but can be calculated if needed
					members: analysis.member_count,
				},
			};

			setGroupInsights(insights);

			// Auto-show insights when present in database
			setShowInsights(true);

			// mark as loaded
			lastLoadedKeyRef.current = groupKey;
		} catch (error) {
			console.error("Error in loadExistingGroupAnalysis:", error);
			// prevent retry loops for this key
			lastLoadedKeyRef.current = groupKey;
		}
	};

	// Check if group is eligible for insights generation
	const isGroupEligibleForInsights = async (group: Group): Promise<boolean> => {
		if (group.members.length === 0) {
			console.log(`❌ Group eligibility: No members found`);
			return false;
		}

		const memberIds = group.members.map((m) => m.mentee_id);
		console.log(
			`🔍 Checking eligibility for ${memberIds.length} members:`,
			memberIds,
		);

		// Validate member IDs before querying - check UUID format
		const uuidRegex =
			/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
		const validMemberIds = memberIds.filter((id) => {
			if (!id || typeof id !== "string" || id.trim() === "") {
				console.warn(`⚠️ Invalid member ID (empty or null): ${id}`);
				return false;
			}
			if (!uuidRegex.test(id)) {
				console.warn(`⚠️ Invalid UUID format for member ID: ${id}`);
				return false;
			}
			return true;
		});

		if (validMemberIds.length === 0) {
			console.log(`❌ Group eligibility: No valid member IDs found`);
			console.log(`⚠️ All member IDs failed validation:`, memberIds);
			return false;
		}

		if (validMemberIds.length < memberIds.length) {
			console.warn(
				`⚠️ Some member IDs were invalid. Using ${validMemberIds.length} of ${memberIds.length} members`,
			);
		}

		console.log(
			`🔍 Checking check-ins for ${validMemberIds.length} valid member IDs:`,
			validMemberIds,
		);

		// Check for at least 1 check-in in the last 28 days
		const { data: checkIns, error: checkInError } = await supabase
			.from("daily_checkins")
			.select("id")
			.in("user_id", validMemberIds)
			.gte(
				"created_at",
				new Date(Date.now() - 28 * 24 * 60 * 60 * 1000).toISOString(),
			)
			.limit(1);

		if (checkInError) {
			console.error(`❌ Error checking check-ins:`, checkInError);
			return false;
		}

		if (!checkIns || checkIns.length === 0) {
			console.log(`❌ No check-ins found for members:`, validMemberIds);
			return false;
		}

		console.log(
			`🔍 Checking reflections for valid member IDs:`,
			validMemberIds,
		);

		// Check for at least 1 completed reflection
		let reflectionQuery = supabase
			.from("reflections")
			.select("id")
			.in("user_id", validMemberIds)
			.eq("is_draft", false);

		// If topicId is provided, filter by topic
		if (topicId) {
			reflectionQuery = reflectionQuery.contains("topic_tags", [topicId]);
			console.log(`🎯 Filtering reflections by topic: ${topicId}`);
		}

		const { data: reflections, error: reflectionError } =
			await reflectionQuery.limit(1);

		if (reflectionError) {
			console.error(`❌ Error checking reflections:`, reflectionError);
			return false;
		}

		if (!reflections || reflections.length === 0) {
			const topicMsg = topicId ? ` for topic ${topicId}` : "";
			console.log(
				`❌ No reflections found for members${topicMsg}:`,
				validMemberIds,
			);
			return false;
		}

		console.log(`✅ Group is eligible for insights generation`);
		return true;

		return true;
	};

	// Save group analysis to database
	const saveGroupAnalysisToDatabase = async (
		group: Group,
		insights: GroupInsights,
		reflectionCheckInCount: number,
		reflectionReportCount: number,
	) => {
		if (!user?.id) return;

		try {
			const today = new Date().toISOString().split("T")[0];
			const groupName = group.name;

			if (topicId) {
				// Save topic-specific analysis
				console.log(`💾 Saving topic-specific analysis for:`, {
					topic_id: topicId,
					group_name: groupName,
					members: group.members.length,
					reflection_checkins: reflectionCheckInCount,
					reflection_reports: reflectionReportCount,
				});

				// For topic analyses, use manual upsert since partial unique indexes might not work with onConflict
				// First, check if analysis exists
				const { data: existingAnalysis } = await supabase
					.from("group_analysis")
					.select("id")
					.eq("mentor_id", user.id)
					.eq("topic_id", topicId)
					.eq("analysis_date", today)
					.maybeSingle();

				const analysisData = {
					mentor_id: user.id,
					topic_id: topicId,
					group_id: null, // NULL for topic-specific analyses
					group_name: groupName,
					analysis_date: today,
					member_count: group.members.length,
					checkin_count: reflectionCheckInCount,
					reflection_count: reflectionReportCount,
					analysis_data: insights.aiAnalysis,
				};

				let error;
				if (existingAnalysis) {
					// Update existing analysis
					const { error: updateError } = await supabase
						.from("group_analysis")
						.update(analysisData)
						.eq("id", existingAnalysis.id);
					error = updateError;
				} else {
					// Insert new analysis
					const { error: insertError } = await supabase
						.from("group_analysis")
						.insert(analysisData);
					error = insertError;
				}

				if (error) {
					console.error(
						"Error saving topic-specific analysis to database:",
						error,
					);
				} else {
					console.log(
						`✅ Topic-specific analysis saved successfully for: ${groupName}`,
					);
				}
			} else {
				// Save group-specific analysis
				console.log(`💾 Saving group analysis for:`, {
					group_id: group.id,
					group_name: groupName,
					members: group.members.length,
					reflection_checkins: reflectionCheckInCount,
					reflection_reports: reflectionReportCount,
				});

				const { error } = await supabase.from("group_analysis").upsert(
					{
						mentor_id: user.id,
						group_id: group.id,
						topic_id: null, // NULL for group-specific analyses
						group_name: groupName,
						analysis_date: today,
						member_count: group.members.length,
						checkin_count: reflectionCheckInCount,
						reflection_count: reflectionReportCount,
						analysis_data: insights.aiAnalysis,
					},
					{
						onConflict: "mentor_id,group_id,analysis_date",
						ignoreDuplicates: false,
					},
				);

				if (error) {
					console.error("Error saving group analysis to database:", error);
				} else {
					console.log(`✅ Group analysis saved successfully for: ${groupName}`);
				}
			}
		} catch (error) {
			console.error("Error in saveGroupAnalysisToDatabase:", error);
		}
	};

	// Generate AI-powered insights for a group
	const generateGroupInsights = async (group: Group) => {
		if (!user?.id) return;

		setGeneratingInsights(true);

		try {
			const memberIds = group.members.map((m) => m.mentee_id);
			console.log(
				`🔍 GroupAnalysisSection: Generating insights for group with ${group.members.length} members`,
			);
			console.log(`👥 Member IDs being used for analysis:`, memberIds);
			console.log(`🎯 Topic ID:`, topicId);

			// Validate member IDs with UUID format check
			const uuidRegex =
				/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
			const validMemberIds = memberIds.filter((id) => {
				if (!id || typeof id !== "string" || id.trim() === "") {
					console.warn(`⚠️ Invalid member ID (empty): ${id}`);
					return false;
				}
				if (!uuidRegex.test(id)) {
					console.warn(`⚠️ Invalid UUID format for member ID: ${id}`);
					return false;
				}
				return true;
			});

			if (validMemberIds.length === 0) {
				throw new Error(
					`No valid member IDs found for analysis. Original IDs: ${memberIds.join(", ")}`,
				);
			}

			if (validMemberIds.length < memberIds.length) {
				console.warn(
					`⚠️ Using ${validMemberIds.length} of ${memberIds.length} member IDs (some were invalid)`,
				);
			}

			console.log(
				`✅ Using ${validMemberIds.length} valid member IDs:`,
				validMemberIds,
			);

			const thirtyDaysAgo = new Date(
				Date.now() - 28 * 24 * 60 * 60 * 1000,
			).toISOString();

			// Fetch ALL check-ins from last 28 days (both general and reflection-based)
			const { data: allCheckInsData, error: checkInError } = await supabase
				.from("daily_checkins")
				.select(`
          id,
          user_id,
          reflection_id,
          general_feeling,
          day_description,
          reflection_feeling,
          reflection_impact,
          reflection_reasons,
          signals_noticed,
          corrective_actions_taken,
          created_at,
          profiles!daily_checkins_user_id_fkey(name)
        `)
				.in("user_id", validMemberIds)
				.gte("created_at", thirtyDaysAgo)
				.order("created_at", { ascending: false });

			if (checkInError) throw checkInError;

			// Separate reflection-based check-ins from general check-ins
			const reflectionBasedCheckIns = (allCheckInsData || []).filter(
				(ci: any) => ci.reflection_id !== null,
			);
			const generalCheckIns = (allCheckInsData || []).filter(
				(ci: any) => ci.reflection_id === null,
			);

			console.log(
				`📊 Check-ins breakdown: ${reflectionBasedCheckIns.length} reflection-based, ${generalCheckIns.length} general`,
			);

			// Fetch all reflections for group members with related data
			let reflectionQuery = supabase
				.from("reflections")
				.select(`
          id,
          user_id,
          title,
          description,
          current_state,
          future_state,
          topic_tags,
          created_at,
          profiles!reflections_user_id_fkey(name)
        `)
				.in("user_id", validMemberIds)
				.eq("is_draft", false)
				.eq("archived", false);

			// If topicId is provided, filter by topic
			if (topicId) {
				reflectionQuery = reflectionQuery.contains("topic_tags", [topicId]);
				console.log(`🔍 Filtering reflections by topic: ${topicId}`);
			}

			const { data: reflectionsData, error: reflectionError } =
				await reflectionQuery;

			if (topicId) {
				console.log(
					`📝 Found ${reflectionsData?.length || 0} topic-specific reflections`,
				);
			}

			if (reflectionError) throw reflectionError;

			// Fetch contributing factors, signals, and actions separately
			const reflectionIds = (reflectionsData || []).map((r) => r.id);

			let contributingFactorsData: Array<{
				reflection_id: string;
				title: string;
				description: string;
			}> = [];
			let signalsData: Array<{
				reflection_id: string;
				title: string;
				description: string;
			}> = [];
			let actionsData: Array<{
				reflection_id: string;
				title: string;
				description: string;
				status?: string;
				priority?: number;
			}> = [];

			if (reflectionIds.length > 0) {
				const { data: factors } = await supabase
					.from("contributing_factors")
					.select("reflection_id, title, description")
					.in("reflection_id", reflectionIds);

				const { data: signals } = await supabase
					.from("signals")
					.select("reflection_id, title, description")
					.in("reflection_id", reflectionIds);

				const { data: actions } = await supabase
					.from("corrective_actions")
					.select("reflection_id, title, description, status, priority")
					.in("reflection_id", reflectionIds);

				contributingFactorsData = factors || [];
				signalsData = signals || [];
				actionsData = actions || [];
			}

			// Enhanced reflection reports (deep, point-in-time)
			const enhancedReflectionReports = (reflectionsData || []).map(
				(reflection: any) => ({
					...reflection,
					user_name: Array.isArray(reflection.profiles)
						? (reflection.profiles[0]?.name ?? "Unknown")
						: (reflection.profiles?.name ?? "Unknown"),
					contributing_factors: contributingFactorsData.filter(
						(f) => f.reflection_id === reflection.id,
					),
					signals: signalsData.filter((s) => s.reflection_id === reflection.id),
					corrective_actions: actionsData.filter(
						(a) => a.reflection_id === reflection.id,
					),
				}),
			);

			// Enhanced reflection-based check-ins (temporal, situational)
			const enhancedReflectionCheckIns = reflectionBasedCheckIns.map(
				(checkIn: any) => ({
					...checkIn,
					user_name: Array.isArray(checkIn.profiles)
						? (checkIn.profiles[0]?.name ?? "Unknown")
						: (checkIn.profiles?.name ?? "Unknown"),
				}),
			);

			// Enhanced general check-ins (for context, if needed)
			const enhancedGeneralCheckIns = generalCheckIns.map((checkIn: any) => ({
				...checkIn,
				user_name: Array.isArray(checkIn.profiles)
					? (checkIn.profiles[0]?.name ?? "Unknown")
					: (checkIn.profiles?.name ?? "Unknown"),
			}));

			console.log(`🧠 Calling AI analysis for group: ${group.name}`);
			console.log(
				`📊 Data: ${enhancedReflectionReports.length} reflection reports, ${enhancedReflectionCheckIns.length} reflection-based check-ins`,
			);
			if (topicId) {
				console.log(`🎯 Topic-specific analysis for topic: ${topicId}`);
				console.log(
					`👥 Analyzing ${validMemberIds.length} members: ${validMemberIds.join(", ")}`,
				);
			}

			// Get the current session for authentication
			const {
				data: { session },
				error: sessionError,
			} = await supabase.auth.getSession();

			if (sessionError || !session) {
				console.error("No valid session found for AI analysis:", sessionError);
				throw new Error("Authentication required for AI analysis");
			}

			// Call the AI edge function in chunks to avoid timeouts
			// New structure: Separate reflection report analysis from reflection check-in analysis, then synthesize
			const sections = [
				// Reflection Report Analysis (deep, point-in-time)
				"reflectionReportAnalysis_cognitiveFrameworks",
				"reflectionReportAnalysis_developmentalInsights",
				"reflectionReportAnalysis_topicSpecific",
				// Reflection Check-in Analysis (temporal, situational)
				"reflectionCheckInAnalysis_applicationTracking",
				"reflectionCheckInAnalysis_temporalPatterns",
				"reflectionCheckInAnalysis_situationalContext",
				"reflectionCheckInAnalysis_realTimeInsights",
				// Integrated Synthesis (combining both)
				"integratedSynthesis_insightToActionGap",
				"integratedSynthesis_topicManifestation",
			];

			console.log(
				`🔄 Starting chunked analysis with ${sections.length} sections`,
			);

			const combinedAnalysis: any = {};
			let failedSections: string[] = [];
			let completedSections = 0;

			// Process each section sequentially
			for (let i = 0; i < sections.length; i++) {
				const section = sections[i];
				try {
					console.log(
						`📋 Analyzing section ${i + 1}/${sections.length}: ${section}`,
					);

					// Update UI with progress (if you have a progress state)
					// You could add: setAnalysisProgress({ current: i + 1, total: sections.length, section });

					const { data: aiResponse, error: aiError } =
						await supabase.functions.invoke("ai-group-insights", {
							headers: {
								Authorization: `Bearer ${session.access_token}`,
							},
							body: {
								groupData: {
									name: group.name,
									description: group.description,
									memberCount: group.members.length,
									topicId: topicId,
									isTopicSpecific: !!topicId,
								},
								reflectionReports: enhancedReflectionReports, // Deep, point-in-time reflection reports
								reflectionCheckIns: enhancedReflectionCheckIns, // Temporal, situational reflection-based check-ins
								generalCheckIns: enhancedGeneralCheckIns, // General check-ins for context (optional)
								section: section, // Request specific section
							},
						});

					if (aiError) {
						console.error(`❌ Error analyzing section ${section}:`, aiError);
						failedSections.push(section);
						continue; // Skip this section and continue with others
					}

					if (aiResponse?.analysis) {
						// Merge the section into combined analysis
						// Handle nested structures (e.g., reflectionReportAnalysis.cognitiveFrameworks)
						const analysisData = aiResponse.analysis;

						// If section name contains underscore, it's a nested structure
						if (section.includes("_")) {
							const [parentKey, childKey] = section.split("_", 2);
							if (!combinedAnalysis[parentKey]) {
								combinedAnalysis[parentKey] = {};
							}

							// Try multiple ways to find the data
							if (
								analysisData[parentKey] &&
								analysisData[parentKey][childKey]
							) {
								// Expected nested structure
								combinedAnalysis[parentKey][childKey] =
									analysisData[parentKey][childKey];
							} else if (analysisData[childKey]) {
								// AI returned flat structure with child key
								console.log(
									`⚠️ Found flat structure ${childKey}, wrapping into ${parentKey}.${childKey}`,
								);
								combinedAnalysis[parentKey][childKey] = analysisData[childKey];
							} else if (analysisData[parentKey]) {
								// Parent exists but child might be at root or nested differently
								// Check if the entire parent object is the data we need
								const parentData = analysisData[parentKey];
								if (parentData[childKey]) {
									combinedAnalysis[parentKey][childKey] = parentData[childKey];
								} else {
									// Parent object might contain the data directly
									console.warn(
										`⚠️ Expected nested structure ${parentKey}.${childKey} not found, but parent exists with keys:`,
										Object.keys(parentData),
									);
									// Try to use the parent object as-is if it has data
									if (Object.keys(parentData).length > 0) {
										combinedAnalysis[parentKey] = {
											...combinedAnalysis[parentKey],
											...parentData,
										};
									}
								}
							} else {
								// Last resort: check if the entire response is what we need
								console.warn(
									`⚠️ Expected nested structure ${parentKey}.${childKey} not found in response. Available keys:`,
									Object.keys(analysisData),
								);
								// Try to extract any useful data
								if (Object.keys(analysisData).length > 0) {
									// Merge whatever we got
									Object.assign(combinedAnalysis, analysisData);
								}
							}
						} else {
							// Legacy flat structure
							Object.assign(combinedAnalysis, analysisData);
						}

						completedSections++;
						console.log(
							`✅ Completed section ${i + 1}/${sections.length}: ${section} (${completedSections}/${sections.length} total)`,
						);
					} else {
						console.warn(`⚠️ No analysis data for section: ${section}`);
						failedSections.push(section);
					}

					// Small delay between requests to avoid rate limiting (reduced from 500ms to 200ms for faster overall)
					if (i < sections.length - 1) {
						// Don't delay after last section
						await new Promise((resolve) => setTimeout(resolve, 200));
					}
				} catch (error) {
					console.error(`❌ Exception analyzing section ${section}:`, error);
					failedSections.push(section);
				}
			}

			// Check if we got any results
			if (Object.keys(combinedAnalysis).length === 0) {
				throw new Error(
					"Failed to generate any analysis sections. All sections failed.",
				);
			}

			// Warn about failed sections but continue with what we have
			if (failedSections.length > 0) {
				console.warn(`⚠️ Some sections failed: ${failedSections.join(", ")}`);
			}

			console.log(
				`✅ Completed ${sections.length - failedSections.length} of ${sections.length} sections`,
			);

			// Structure the insights
			const insights: GroupInsights = {
				groupId: group.id,
				groupName: group.name,
				generatedAt: new Date().toISOString(),
				aiAnalysis: combinedAnalysis,
				metadata: {
					reflectionReports: enhancedReflectionReports.length,
					reflectionCheckIns: enhancedReflectionCheckIns.length,
					generalCheckIns: enhancedGeneralCheckIns.length,
					members: group.members.length,
					sectionsCompleted: sections.length - failedSections.length,
					sectionsTotal: sections.length,
					failedSections:
						failedSections.length > 0 ? failedSections : undefined,
				},
			};

			// Save analysis to database
			await saveGroupAnalysisToDatabase(
				group,
				insights,
				enhancedReflectionCheckIns.length,
				enhancedReflectionReports.length,
			);

			setGroupInsights(insights);
			setShowInsights(true);

			console.log(
				"✅ AI-powered group insights generated and saved successfully",
			);
		} catch (error) {
			console.error("Error generating AI insights:", error);
			const errorMessage =
				error instanceof Error ? error.message : "Failed to generate insights";
			alert(`Failed to generate insights: ${errorMessage}. Please try again.`);
		} finally {
			setGeneratingInsights(false);
		}
	};

	// Helper function to get health status color
	const getHealthStatusColor = (health: string) => {
		switch (health) {
			case "excellent":
				return "text-green-600";
			case "good":
				return "text-blue-600";
			case "concerning":
				return "text-yellow-600";
			case "critical":
				return "text-red-600";
			default:
				return "text-gray-600";
		}
	};

	// Helper function to get trajectory color
	const getTrajectoryColor = (trajectory: string) => {
		switch (trajectory) {
			case "strongly_improving":
				return "text-green-600";
			case "improving":
				return "text-green-500";
			case "stable":
				return "text-blue-600";
			case "declining":
				return "text-yellow-600";
			case "rapidly_declining":
				return "text-red-600";
			default:
				return "text-gray-600";
		}
	};

	// Helper function to get priority badge color
	const getPriorityBadgeVariant = (priority: string) => {
		switch (priority) {
			case "critical":
				return "destructive";
			case "high":
				return "destructive";
			case "medium":
				return "secondary";
			case "low":
				return "outline";
			default:
				return "outline";
		}
	};

	return (
		<div>
			<div className="flex items-center justify-between mb-3">
				<h4 className="text-base font-medium flex items-center space-x-2">
					<Brain className="w-4 h-4" />
					<span>Group Analysis</span>
				</h4>
				<div className="flex space-x-2">
					{groupInsights && !showInsights && (
						<Button
							size="sm"
							variant="secondary"
							onClick={() => setShowInsights(true)}
							className="text-sm"
						>
							<FileText className="w-3 h-3 mr-1" />
							View Analysis
						</Button>
					)}
					<Button
						size="sm"
						variant="outline"
						onClick={async () => {
							if (!generatingInsights) {
								const eligible = await isGroupEligibleForInsights(group);
								if (eligible) {
									generateGroupInsights(group);
								} else {
									const topicMsg = topicId
										? "This topic needs at least 1 completed reflection tagged with this topic and 1 check-in from assigned team members in the last 28 days to generate insights. Make sure the topic has been assigned to groups with active members."
										: "This group needs at least 1 completed reflection and 1 check-in in the last 28 days to generate insights.";
									alert(topicMsg);
								}
							}
						}}
						disabled={generatingInsights}
						className="text-sm"
					>
						{generatingInsights ? (
							<>
								<div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary mr-1"></div>
								Generating...
							</>
						) : (
							<>
								<BarChart3 className="w-3 h-3 mr-1" />
								{groupInsights ? "Update Analysis" : "Generate Analysis"}
							</>
						)}
					</Button>
				</div>
			</div>

			{/* AI-Powered Insights Report */}
			{groupInsights && showInsights && (
				<>
					<div className="flex items-center justify-end mb-3">
						<Button
							size="sm"
							variant="ghost"
							onClick={() => setShowInsights(false)}
						>
							<ChevronUp className="w-4 h-4" />
						</Button>
					</div>

					{/* Executive Summary */}
					{groupInsights.aiAnalysis.executiveSummary && (
						<div className="mt-4 p-4 bg-background rounded border">
							<h6 className="text-base font-medium mb-3 flex items-center">
								<TrendingUp className="w-4 h-4 mr-2" />
								Executive Summary
							</h6>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div>
									<div className="flex items-center space-x-2 mb-2">
										<span className="text-gray-700">Overall Health:</span>
										<Badge
											variant={
												groupInsights.aiAnalysis.executiveSummary
													.overallHealth === "excellent"
													? "outline"
													: groupInsights.aiAnalysis.executiveSummary
																.overallHealth === "good"
														? "secondary"
														: groupInsights.aiAnalysis.executiveSummary
																	.overallHealth === "concerning"
															? "secondary"
															: "destructive"
											}
											className={`text-xs ${getHealthStatusColor(groupInsights.aiAnalysis.executiveSummary.overallHealth)}`}
										>
											{groupInsights.aiAnalysis.executiveSummary.overallHealth}
										</Badge>
									</div>
									<div className="flex items-center space-x-2">
										<span className="text-gray-700">Priority:</span>
										<Badge variant="outline" className="text-xs">
											{
												groupInsights.aiAnalysis.executiveSummary
													.timeframePriority
											}
										</Badge>
									</div>
								</div>
								<div className="text-sm text-muted-foreground">
									<strong>Data Points:</strong>{" "}
									{groupInsights.metadata.reflectionReports} reflection reports,{" "}
									{groupInsights.metadata.reflectionCheckIns} reflection-based
									check-ins, {groupInsights.metadata.members} members
								</div>
							</div>

							<div className="mt-3 space-y-2">
								<div>
									<span className="font-medium text-gray-900">
										Key Findings:
									</span>
									<ul className="list-disc list-inside text-gray-700 leading-relaxed mt-1 space-y-2.5 pl-2">
										{(
											groupInsights.aiAnalysis.executiveSummary.keyFindings ||
											[]
										).map((finding, idx) => (
											<li key={idx} className="leading-relaxed">
												{finding}
											</li>
										))}
									</ul>
								</div>
							</div>
						</div>
					)}

					{/* Future State Vision */}
					{groupInsights.aiAnalysis.futureStateVisioning && (
						<div className="mt-6 p-4 bg-background rounded border">
							<h6 className="text-base font-medium mb-3">
								Future State Vision
							</h6>
							<div className="space-y-3">
								{groupInsights.aiAnalysis.futureStateVisioning
									.currentTrajectory && (
									<div>
										<span className="font-medium text-gray-900">
											Current Trajectory:
										</span>
										<p className="text-gray-700 leading-relaxed mt-1">
											{
												groupInsights.aiAnalysis.futureStateVisioning
													.currentTrajectory
											}
										</p>
									</div>
								)}
								{groupInsights.aiAnalysis.futureStateVisioning
									.pathwayToSuccess && (
									<div>
										<span className="font-medium text-gray-900">
											Pathway to Success:
										</span>
										<p className="text-gray-700 leading-relaxed mt-1">
											{
												groupInsights.aiAnalysis.futureStateVisioning
													.pathwayToSuccess
											}
										</p>
									</div>
								)}
								{groupInsights.aiAnalysis.futureStateVisioning.keyMilestones &&
									groupInsights.aiAnalysis.futureStateVisioning.keyMilestones
										.length > 0 && (
										<div>
											<span className="font-medium text-gray-900">
												Key Milestones:
											</span>
											<ul className="list-disc list-inside text-gray-700 leading-relaxed mt-1">
												{groupInsights.aiAnalysis.futureStateVisioning.keyMilestones
													.slice(0, 4)
													.map((milestone, idx) => (
														<li key={idx}>{milestone}</li>
													))}
											</ul>
										</div>
									)}
							</div>
						</div>
					)}

					{/* Sentiment Analysis with Chart */}
					{groupInsights.aiAnalysis.sentimentAnalysis && (
						<div className="mt-6 p-4 bg-background rounded border">
							<h6 className="text-base font-medium mb-3">
								Sentiment & Mood Analysis
							</h6>
							<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
								<div className="space-y-3">
									{groupInsights.aiAnalysis.sentimentAnalysis
										.overallTrajectory && (
										<div className="flex items-center justify-between">
											<span className="text-gray-700">Trajectory:</span>
											<span
												className={`font-medium ${getTrajectoryColor(groupInsights.aiAnalysis.sentimentAnalysis.overallTrajectory)}`}
											>
												{groupInsights.aiAnalysis.sentimentAnalysis.overallTrajectory.replace(
													"_",
													" ",
												)}
											</span>
										</div>
									)}
									{groupInsights.aiAnalysis.sentimentAnalysis
										.volatilityPattern && (
										<div className="flex items-center justify-between">
											<span className="text-gray-700">Volatility:</span>
											<Badge variant="outline" className="text-xs">
												{
													groupInsights.aiAnalysis.sentimentAnalysis
														.volatilityPattern
												}
											</Badge>
										</div>
									)}
									{groupInsights.aiAnalysis.sentimentAnalysis
										.emotionalUndercurrents &&
										groupInsights.aiAnalysis.sentimentAnalysis
											.emotionalUndercurrents.length > 0 && (
											<div className="space-y-1">
												<span className="font-medium text-gray-900">
													Emotional Patterns:
												</span>
												<div className="text-sm text-gray-700 leading-relaxed">
													{groupInsights.aiAnalysis.sentimentAnalysis.emotionalUndercurrents
														.slice(0, 3)
														.map((pattern, idx) => (
															<div key={idx}>• {pattern}</div>
														))}
												</div>
											</div>
										)}
								</div>

								{/* Sentiment Trend Chart */}
								{groupInsights.aiAnalysis.sentimentAnalysis.chartData
									?.sentimentTrend &&
									groupInsights.aiAnalysis.sentimentAnalysis.chartData
										.sentimentTrend.length > 0 && (
										<div className="h-48">
											<div className="text-base font-medium mb-2">
												Sentiment Trend
											</div>
											<ResponsiveContainer width="100%" height="100%">
												<LineChart
													data={
														groupInsights.aiAnalysis.sentimentAnalysis.chartData
															.sentimentTrend
													}
												>
													<CartesianGrid
														strokeDasharray="3 3"
														className="opacity-30"
													/>
													<XAxis
														dataKey="date"
														tick={{ fontSize: 10 }}
														tickFormatter={(date) =>
															new Date(date).toLocaleDateString("en-US", {
																month: "short",
																day: "numeric",
															})
														}
													/>
													<YAxis tick={{ fontSize: 10 }} domain={[-3, 3]} />
													<Tooltip
														content={({ active, payload, label }) => {
															if (!active || !payload || payload.length === 0)
																return null;
															const data = payload[0].payload;
															return (
																<div className="rounded-md border bg-popover px-3 py-2 text-xs shadow-md">
																	<div className="font-medium">
																		{new Date(label).toLocaleDateString()}
																	</div>
																	<div>Member: {data.memberName}</div>
																	<div>Sentiment: {data.averageSentiment}</div>
																	{data.keyEvent && (
																		<div className="text-primary">
																			Event: {data.keyEvent}
																		</div>
																	)}
																</div>
															);
														}}
													/>
													<Line
														type="monotone"
														dataKey="averageSentiment"
														stroke="hsl(var(--primary))"
														strokeWidth={2}
														dot={{
															fill: "hsl(var(--primary))",
															strokeWidth: 2,
															r: 1.5,
														}}
													/>
												</LineChart>
											</ResponsiveContainer>
										</div>
									)}
							</div>
						</div>
					)}

					{/* Thematic Analysis */}
					{groupInsights.aiAnalysis.thematicAnalysis &&
						groupInsights.aiAnalysis.thematicAnalysis.emergingNarratives &&
						groupInsights.aiAnalysis.thematicAnalysis.emergingNarratives
							.length > 0 && (
							<div className="mt-6 p-4 bg-background rounded border">
								<h6 className="text-base font-medium mb-3">
									Thematic Analysis
								</h6>
								<div className="space-y-3">
									{groupInsights.aiAnalysis.thematicAnalysis.emergingNarratives
										.slice(0, 3)
										.map((narrative, idx) => (
											<div key={idx} className="p-3 bg-muted/50 rounded">
												<div className="flex items-center justify-between mb-2">
													<span className="font-medium text-gray-900">
														{narrative.theme}
													</span>
													<Badge variant="outline" className="text-sm">
														{narrative.prevalence}
													</Badge>
												</div>
												<p className="text-sm text-gray-700 leading-relaxed mb-2">
													{narrative.psychologicalSignificance}
												</p>
												<div className="text-sm text-muted-foreground">
													<strong>Evolution:</strong> {narrative.evolution}
												</div>
											</div>
										))}
								</div>
							</div>
						)}

					{/* Priority Recommendations with Priority Chart */}
					{groupInsights.aiAnalysis.actionableRecommendations &&
						groupInsights.aiAnalysis.actionableRecommendations.length > 0 && (
							<div className="mt-6 p-4 bg-background rounded border">
								<h6 className="text-base font-medium mb-3">
									Actionable Recommendations
								</h6>

								<div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
									<div className="lg:col-span-3 space-y-3">
										{groupInsights.aiAnalysis.actionableRecommendations
											.slice(0, 5)
											.map((rec, idx) => (
												<div key={idx} className="p-3 bg-muted/50 rounded">
													<div className="flex items-center justify-between mb-2">
														<span className="font-medium text-gray-900">
															{rec.title}
														</span>
														<Badge
															variant={getPriorityBadgeVariant(rec.priority)}
															className="text-sm"
														>
															{rec.priority}
														</Badge>
													</div>
													<p className="text-sm text-gray-700 leading-relaxed mb-2">
														{rec.description}
													</p>
													<div className="text-sm text-muted-foreground space-y-1">
														<div>
															<strong>Rationale:</strong> {rec.rationale}
														</div>
														<div>
															<strong>Expected Outcome:</strong>{" "}
															{rec.expectedOutcome}
														</div>
														<div>
															<strong>Timeline:</strong>{" "}
															{rec.implementation?.timeline || "Not specified"}
														</div>
													</div>
												</div>
											))}
									</div>
								</div>
							</div>
						)}

					{/* NEW: Reflection Report Analysis (Deep, Point-in-Time) */}
					{groupInsights.aiAnalysis.reflectionReportAnalysis && (
						<div className="mt-6 space-y-4">
							<div className="flex items-center space-x-2 mb-4">
								<FileText className="w-5 h-5 text-blue-600" />
								<h5 className="text-lg font-semibold">
									Reflection Report Analysis
								</h5>
								<Badge variant="outline" className="text-xs">
									Deep Insights
								</Badge>
							</div>
							<p className="text-sm text-muted-foreground mb-4">
								Analysis of structured reflection reports revealing cognitive
								frameworks, mental models, and deep patterns.
							</p>

							{/* Cognitive Frameworks */}
							{groupInsights.aiAnalysis.reflectionReportAnalysis
								.cognitiveFrameworks && (
								<Card className="border-l-4 border-l-blue-500">
									<CardHeader className="pb-3">
										<CardTitle className="text-base flex items-center justify-between">
											<div className="flex items-center space-x-2">
												<Brain className="w-4 h-4" />
												<span>Cognitive Frameworks & Mental Models</span>
											</div>
											<Badge variant="outline" className="text-xs">
												From Reflection Reports
											</Badge>
										</CardTitle>
									</CardHeader>
									<CardContent className="space-y-4">
										{groupInsights.aiAnalysis.reflectionReportAnalysis
											.cognitiveFrameworks.dominantMentalModels &&
											groupInsights.aiAnalysis.reflectionReportAnalysis
												.cognitiveFrameworks.dominantMentalModels.length >
												0 && (
												<div>
													<h6 className="text-sm font-semibold mb-2 text-gray-900">
														Dominant Mental Models
													</h6>
													<div className="space-y-2">
														{groupInsights.aiAnalysis.reflectionReportAnalysis.cognitiveFrameworks.dominantMentalModels.map(
															(model: any, idx: number) => (
																<div
																	key={idx}
																	className="p-3 bg-muted/50 rounded-lg"
																>
																	<div className="flex items-start justify-between mb-1">
																		<span className="font-medium text-sm">
																			{model.model}
																		</span>
																		<Badge
																			variant="outline"
																			className="text-xs"
																		>
																			{model.prevalence}
																		</Badge>
																	</div>
																	{model.examples &&
																		model.examples.length > 0 && (
																			<div className="text-xs text-muted-foreground mt-1">
																				<span className="font-medium">
																					Examples:{" "}
																				</span>
																				{model.examples.slice(0, 2).join(", ")}
																			</div>
																		)}
																</div>
															),
														)}
													</div>
												</div>
											)}
										{groupInsights.aiAnalysis.reflectionReportAnalysis
											.cognitiveFrameworks.cognitiveBiases &&
											groupInsights.aiAnalysis.reflectionReportAnalysis
												.cognitiveFrameworks.cognitiveBiases.length > 0 && (
												<div>
													<h6 className="text-sm font-semibold mb-2 text-gray-900">
														Cognitive Biases Identified
													</h6>
													<div className="flex flex-wrap gap-2">
														{groupInsights.aiAnalysis.reflectionReportAnalysis.cognitiveFrameworks.cognitiveBiases.map(
															(bias: any, idx: number) => (
																<Badge
																	key={idx}
																	variant={
																		bias.impact === "negative"
																			? "destructive"
																			: "outline"
																	}
																	className="text-xs"
																>
																	{bias.bias}
																</Badge>
															),
														)}
													</div>
												</div>
											)}
									</CardContent>
								</Card>
							)}

							{/* Developmental Insights */}
							{groupInsights.aiAnalysis.reflectionReportAnalysis
								.developmentalInsights && (
								<Card className="border-l-4 border-l-green-500">
									<CardHeader className="pb-3">
										<CardTitle className="text-base flex items-center justify-between">
											<div className="flex items-center space-x-2">
												<TrendingUp className="w-4 h-4" />
												<span>Developmental Insights</span>
											</div>
											<Badge variant="outline" className="text-xs">
												From Reflection Reports
											</Badge>
										</CardTitle>
									</CardHeader>
									<CardContent className="space-y-4">
										{groupInsights.aiAnalysis.reflectionReportAnalysis
											.developmentalInsights.maturityDistribution && (
											<div>
												<div className="flex items-center justify-between mb-2">
													<h6 className="text-sm font-semibold text-gray-900">
														Maturity Distribution
													</h6>
													<Badge variant="outline" className="text-xs">
														From Reflection Reports
													</Badge>
												</div>
												<p className="text-xs text-muted-foreground mb-3">
													Distribution of members' developmental maturity levels
													based on their reflection depth, self-awareness, and
													growth indicators.
												</p>
												<div className="grid grid-cols-3 gap-3">
													<div className="text-center p-3 bg-green-50 dark:bg-green-950 rounded">
														<div className="text-2xl font-bold text-green-600">
															{groupInsights.aiAnalysis.reflectionReportAnalysis
																.developmentalInsights.maturityDistribution
																.high || 0}
														</div>
														<div className="text-xs text-muted-foreground mt-1">
															High
														</div>
													</div>
													<div className="text-center p-3 bg-yellow-50 dark:bg-yellow-950 rounded">
														<div className="text-2xl font-bold text-yellow-600">
															{groupInsights.aiAnalysis.reflectionReportAnalysis
																.developmentalInsights.maturityDistribution
																.medium || 0}
														</div>
														<div className="text-xs text-muted-foreground mt-1">
															Medium
														</div>
													</div>
													<div className="text-center p-3 bg-orange-50 dark:bg-orange-950 rounded">
														<div className="text-2xl font-bold text-orange-600">
															{groupInsights.aiAnalysis.reflectionReportAnalysis
																.developmentalInsights.maturityDistribution
																.low || 0}
														</div>
														<div className="text-xs text-muted-foreground mt-1">
															Low
														</div>
													</div>
												</div>
											</div>
										)}
										{groupInsights.aiAnalysis.reflectionReportAnalysis
											.developmentalInsights.growthIndicators &&
											groupInsights.aiAnalysis.reflectionReportAnalysis
												.developmentalInsights.growthIndicators.length > 0 && (
												<div>
													<h6 className="text-sm font-semibold mb-3 text-gray-900">
														Growth Indicators
													</h6>
													<ul className="list-disc list-inside text-sm text-gray-700 space-y-2.5 pl-2">
														{groupInsights.aiAnalysis.reflectionReportAnalysis.developmentalInsights.growthIndicators.map(
															(indicator: string, idx: number) => (
																<li key={idx} className="leading-relaxed">
																	{indicator}
																</li>
															),
														)}
													</ul>
												</div>
											)}
									</CardContent>
								</Card>
							)}

							{/* Topic-Specific Insights */}
							{groupInsights.aiAnalysis.reflectionReportAnalysis
								.topicSpecificInsights && (
								<Card className="border-l-4 border-l-indigo-500">
									<CardHeader className="pb-3">
										<CardTitle className="text-base flex items-center justify-between">
											<div className="flex items-center space-x-2">
												<BarChart3 className="w-4 h-4" />
												<span>Topic-Specific Insights</span>
											</div>
											<Badge variant="outline" className="text-xs">
												From Reflection Reports
											</Badge>
										</CardTitle>
									</CardHeader>
									<CardContent className="space-y-3">
										{groupInsights.aiAnalysis.reflectionReportAnalysis
											.topicSpecificInsights.howTopicManifests && (
											<div>
												<h6 className="text-sm font-semibold mb-2 text-gray-900">
													How Topic Manifests
												</h6>
												<p className="text-sm text-gray-700 leading-relaxed">
													{
														groupInsights.aiAnalysis.reflectionReportAnalysis
															.topicSpecificInsights.howTopicManifests
													}
												</p>
											</div>
										)}
										{groupInsights.aiAnalysis.reflectionReportAnalysis
											.topicSpecificInsights.unknownAspectsRevealed &&
											groupInsights.aiAnalysis.reflectionReportAnalysis
												.topicSpecificInsights.unknownAspectsRevealed.length >
												0 && (
												<div>
													<h6 className="text-sm font-semibold mb-3 text-gray-900">
														Unknown Aspects Revealed
													</h6>
													<ul className="list-disc list-inside text-sm text-gray-700 space-y-2.5 pl-2">
														{groupInsights.aiAnalysis.reflectionReportAnalysis.topicSpecificInsights.unknownAspectsRevealed.map(
															(aspect: string, idx: number) => (
																<li key={idx} className="leading-relaxed">
																	{aspect}
																</li>
															),
														)}
													</ul>
												</div>
											)}
										{groupInsights.aiAnalysis.reflectionReportAnalysis
											.topicSpecificInsights.deeperUnderstanding && (
											<div>
												<h6 className="text-sm font-semibold mb-2 text-gray-900">
													Deeper Understanding
												</h6>
												<p className="text-sm text-gray-700 leading-relaxed">
													{
														groupInsights.aiAnalysis.reflectionReportAnalysis
															.topicSpecificInsights.deeperUnderstanding
													}
												</p>
											</div>
										)}
									</CardContent>
								</Card>
							)}
						</div>
					)}

					{/* NEW: Reflection Check-in Analysis (Temporal, Situational) */}
					{groupInsights.aiAnalysis.reflectionCheckInAnalysis && (
						<div className="mt-8 space-y-4">
							<div className="flex items-center space-x-2 mb-4">
								<BarChart3 className="w-5 h-5 text-orange-600" />
								<h5 className="text-lg font-semibold">
									Reflection Check-in Analysis
								</h5>
								<Badge variant="outline" className="text-xs">
									Real-Time Patterns
								</Badge>
							</div>
							<p className="text-sm text-muted-foreground mb-4">
								Analysis of reflection-based check-ins showing real-time
								application, triggers, and situational patterns.
							</p>

							{/* Application Tracking */}
							{groupInsights.aiAnalysis.reflectionCheckInAnalysis
								.applicationTracking && (
								<Card className="border-l-4 border-l-orange-500">
									<CardHeader className="pb-3">
										<CardTitle className="text-base flex items-center justify-between">
											<div className="flex items-center space-x-2">
												<Target className="w-4 h-4" />
												<span>Application & Integration Tracking</span>
											</div>
											<Badge variant="outline" className="text-xs">
												From Reflection Check-ins
											</Badge>
										</CardTitle>
									</CardHeader>
									<CardContent className="space-y-5">
										{groupInsights.aiAnalysis.reflectionCheckInAnalysis
											.applicationTracking.actionFollowThrough && (
											<div className="p-5 bg-muted/50 rounded-lg border">
												<h6 className="text-sm font-semibold mb-4 text-gray-900">
													Action Follow-Through
												</h6>
												<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
													<div className="space-y-2">
														<div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
															Planned
														</div>
														<p className="text-sm text-gray-700 leading-relaxed">
															{typeof groupInsights.aiAnalysis
																.reflectionCheckInAnalysis.applicationTracking
																.actionFollowThrough.planned === "string"
																? groupInsights.aiAnalysis
																		.reflectionCheckInAnalysis
																		.applicationTracking.actionFollowThrough
																		.planned
																: `${groupInsights.aiAnalysis.reflectionCheckInAnalysis.applicationTracking.actionFollowThrough.planned || 0} actions planned`}
														</p>
													</div>
													<div className="space-y-2">
														<div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
															Taken
														</div>
														<p className="text-sm text-gray-700 leading-relaxed">
															{typeof groupInsights.aiAnalysis
																.reflectionCheckInAnalysis.applicationTracking
																.actionFollowThrough.taken === "string"
																? groupInsights.aiAnalysis
																		.reflectionCheckInAnalysis
																		.applicationTracking.actionFollowThrough
																		.taken
																: `${groupInsights.aiAnalysis.reflectionCheckInAnalysis.applicationTracking.actionFollowThrough.taken || 0} actions taken`}
														</p>
													</div>
												</div>
												{groupInsights.aiAnalysis.reflectionCheckInAnalysis
													.applicationTracking.actionFollowThrough.gap && (
													<div className="pt-3 border-t">
														<div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
															Gap Analysis
														</div>
														<p className="text-sm text-gray-700 leading-relaxed">
															{
																groupInsights.aiAnalysis
																	.reflectionCheckInAnalysis.applicationTracking
																	.actionFollowThrough.gap
															}
														</p>
													</div>
												)}
											</div>
										)}
									</CardContent>
								</Card>
							)}

							{/* Temporal Patterns */}
							{groupInsights.aiAnalysis.reflectionCheckInAnalysis
								.temporalPatterns && (
								<Card className="border-l-4 border-l-amber-500">
									<CardHeader className="pb-3">
										<CardTitle className="text-base flex items-center justify-between">
											<div className="flex items-center space-x-2">
												<TrendingUp className="w-4 h-4" />
												<span>Temporal Patterns</span>
											</div>
											<Badge variant="outline" className="text-xs">
												From Reflection Check-ins
											</Badge>
										</CardTitle>
									</CardHeader>
									<CardContent className="space-y-4">
										{groupInsights.aiAnalysis.reflectionCheckInAnalysis
											.temporalPatterns.triggerPatterns &&
											groupInsights.aiAnalysis.reflectionCheckInAnalysis
												.temporalPatterns.triggerPatterns.length > 0 && (
												<div>
													<h6 className="text-sm font-semibold mb-2 text-gray-900">
														Trigger Patterns
													</h6>
													<div className="space-y-2">
														{groupInsights.aiAnalysis.reflectionCheckInAnalysis.temporalPatterns.triggerPatterns.map(
															(trigger: any, idx: number) => (
																<div
																	key={idx}
																	className="p-3 bg-muted/50 rounded-lg"
																>
																	<div className="flex items-center justify-between mb-1">
																		<span className="font-medium text-sm">
																			{trigger.trigger}
																		</span>
																		<Badge
																			variant={
																				trigger.impact === "negative"
																					? "destructive"
																					: trigger.impact === "positive"
																						? "default"
																						: "secondary"
																			}
																			className="text-xs"
																		>
																			{trigger.impact}
																		</Badge>
																	</div>
																	<div className="text-xs text-muted-foreground">
																		{trigger.frequency}
																	</div>
																</div>
															),
														)}
													</div>
												</div>
											)}
										{groupInsights.aiAnalysis.reflectionCheckInAnalysis
											.temporalPatterns.stressCycles && (
											<div className="p-4 bg-muted/30 rounded-lg">
												<h6 className="text-sm font-semibold mb-2 text-gray-900">
													Stress Cycles
												</h6>
												<div className="space-y-2">
													<div className="text-sm">
														{
															groupInsights.aiAnalysis.reflectionCheckInAnalysis
																.temporalPatterns.stressCycles.pattern
														}
													</div>
													<div className="flex items-center space-x-2">
														<Badge variant="outline" className="text-xs">
															{
																groupInsights.aiAnalysis
																	.reflectionCheckInAnalysis.temporalPatterns
																	.stressCycles.frequency
															}
														</Badge>
														{groupInsights.aiAnalysis.reflectionCheckInAnalysis
															.temporalPatterns.stressCycles.peakTimes &&
															groupInsights.aiAnalysis.reflectionCheckInAnalysis
																.temporalPatterns.stressCycles.peakTimes
																.length > 0 && (
																<span className="text-xs text-muted-foreground">
																	Peaks:{" "}
																	{groupInsights.aiAnalysis.reflectionCheckInAnalysis.temporalPatterns.stressCycles.peakTimes.join(
																		", ",
																	)}
																</span>
															)}
													</div>
												</div>
											</div>
										)}
									</CardContent>
								</Card>
							)}

							{/* Situational Context */}
							{groupInsights.aiAnalysis.reflectionCheckInAnalysis
								.situationalContext && (
								<Card className="border-l-4 border-l-teal-500">
									<CardHeader className="pb-3">
										<CardTitle className="text-base flex items-center justify-between">
											<div className="flex items-center space-x-2">
												<Brain className="w-4 h-4" />
												<span>Situational Context</span>
											</div>
											<Badge variant="outline" className="text-xs">
												From Reflection Check-ins
											</Badge>
										</CardTitle>
									</CardHeader>
									<CardContent className="space-y-4">
										{groupInsights.aiAnalysis.reflectionCheckInAnalysis
											.situationalContext.contextSpecificManifestations &&
											groupInsights.aiAnalysis.reflectionCheckInAnalysis
												.situationalContext.contextSpecificManifestations
												.length > 0 && (
												<div>
													<h6 className="text-sm font-semibold mb-2 text-gray-900">
														Context-Specific Manifestations
													</h6>
													<div className="space-y-2">
														{groupInsights.aiAnalysis.reflectionCheckInAnalysis.situationalContext.contextSpecificManifestations.map(
															(manifestation: any, idx: number) => (
																<div
																	key={idx}
																	className="p-3 bg-muted/50 rounded-lg"
																>
																	<div className="font-medium text-sm mb-1">
																		{manifestation.context}
																	</div>
																	<div className="text-xs text-muted-foreground">
																		{manifestation.howTopicShowsUp}
																	</div>
																</div>
															),
														)}
													</div>
												</div>
											)}
										{groupInsights.aiAnalysis.reflectionCheckInAnalysis
											.situationalContext.environmentalTriggers &&
											groupInsights.aiAnalysis.reflectionCheckInAnalysis
												.situationalContext.environmentalTriggers.length >
												0 && (
												<div>
													<h6 className="text-sm font-semibold mb-3 text-gray-900">
														Environmental Triggers
													</h6>
													<ul className="list-disc list-inside text-sm text-gray-700 space-y-2.5 pl-2">
														{groupInsights.aiAnalysis.reflectionCheckInAnalysis.situationalContext.environmentalTriggers.map(
															(trigger: string, idx: number) => (
																<li key={idx} className="leading-relaxed">
																	{trigger}
																</li>
															),
														)}
													</ul>
												</div>
											)}
									</CardContent>
								</Card>
							)}

							{/* Real-Time Insights */}
							{groupInsights.aiAnalysis.reflectionCheckInAnalysis
								.realTimeInsights && (
								<Card className="border-l-4 border-l-pink-500">
									<CardHeader className="pb-3">
										<CardTitle className="text-base flex items-center justify-between">
											<div className="flex items-center space-x-2">
												<BarChart3 className="w-4 h-4" />
												<span>Real-Time Insights</span>
											</div>
											<Badge variant="outline" className="text-xs">
												From Reflection Check-ins
											</Badge>
										</CardTitle>
									</CardHeader>
									<CardContent className="space-y-4">
										<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
											{groupInsights.aiAnalysis.reflectionCheckInAnalysis
												.realTimeInsights.whatsWorkingNow &&
												groupInsights.aiAnalysis.reflectionCheckInAnalysis
													.realTimeInsights.whatsWorkingNow.length > 0 && (
													<div className="p-4 bg-green-50 dark:bg-green-950 rounded-lg">
														<h6 className="text-sm font-semibold mb-2 text-green-900 dark:text-green-100">
															What's Working Now
														</h6>
														<ul className="list-disc list-inside text-sm text-green-800 dark:text-green-200 space-y-2.5 pl-2">
															{groupInsights.aiAnalysis.reflectionCheckInAnalysis.realTimeInsights.whatsWorkingNow.map(
																(item: string, idx: number) => (
																	<li key={idx} className="leading-relaxed">
																		{item}
																	</li>
																),
															)}
														</ul>
													</div>
												)}
											{groupInsights.aiAnalysis.reflectionCheckInAnalysis
												.realTimeInsights.whatsNotWorking &&
												groupInsights.aiAnalysis.reflectionCheckInAnalysis
													.realTimeInsights.whatsNotWorking.length > 0 && (
													<div className="p-4 bg-red-50 dark:bg-red-950 rounded-lg">
														<h6 className="text-sm font-semibold mb-2 text-red-900 dark:text-red-100">
															What's Not Working
														</h6>
														<ul className="list-disc list-inside text-sm text-red-800 dark:text-red-200 space-y-2.5 pl-2">
															{groupInsights.aiAnalysis.reflectionCheckInAnalysis.realTimeInsights.whatsNotWorking.map(
																(item: string, idx: number) => (
																	<li key={idx} className="leading-relaxed">
																		{item}
																	</li>
																),
															)}
														</ul>
													</div>
												)}
										</div>
										{groupInsights.aiAnalysis.reflectionCheckInAnalysis
											.realTimeInsights.emergingPatterns &&
											groupInsights.aiAnalysis.reflectionCheckInAnalysis
												.realTimeInsights.emergingPatterns.length > 0 && (
												<div>
													<h6 className="text-sm font-semibold mb-2 text-gray-900">
														Emerging Patterns
													</h6>
													<ul className="list-disc list-inside text-sm text-gray-700 space-y-2.5 pl-2">
														{groupInsights.aiAnalysis.reflectionCheckInAnalysis.realTimeInsights.emergingPatterns.map(
															(pattern: string, idx: number) => (
																<li key={idx} className="leading-relaxed">
																	{pattern}
																</li>
															),
														)}
													</ul>
												</div>
											)}
									</CardContent>
								</Card>
							)}
						</div>
					)}

					{/* NEW: Integrated Synthesis */}
					{groupInsights.aiAnalysis.integratedSynthesis && (
						<div className="mt-8 space-y-4">
							<div className="flex items-center space-x-2 mb-4">
								<Brain className="w-5 h-5 text-indigo-600" />
								<h5 className="text-lg font-semibold">Integrated Synthesis</h5>
								<Badge variant="outline" className="text-xs">
									Action Plan
								</Badge>
							</div>
							<p className="text-sm text-muted-foreground mb-4">
								Combined insights from reflection reports and check-ins to
								create actionable coaching strategies.
							</p>

							{/* Insight to Action Gap */}
							{groupInsights.aiAnalysis.integratedSynthesis
								.insightToActionGap && (
								<Card className="border-l-4 border-l-indigo-500">
									<CardHeader className="pb-3">
										<CardTitle className="text-base flex items-center justify-between">
											<div className="flex items-center space-x-2">
												<Target className="w-4 h-4" />
												<span>Insight to Action Gap</span>
											</div>
											<Badge variant="outline" className="text-xs">
												Combined Analysis
											</Badge>
										</CardTitle>
									</CardHeader>
									<CardContent className="space-y-4">
										{groupInsights.aiAnalysis.integratedSynthesis
											.insightToActionGap.barriersToApplication &&
											groupInsights.aiAnalysis.integratedSynthesis
												.insightToActionGap.barriersToApplication.length >
												0 && (
												<div>
													<h6 className="text-sm font-semibold mb-3 text-gray-900">
														Barriers to Application
													</h6>
													<ul className="list-disc list-inside text-sm text-gray-700 space-y-2.5 pl-2">
														{groupInsights.aiAnalysis.integratedSynthesis.insightToActionGap.barriersToApplication.map(
															(barrier: string, idx: number) => (
																<li key={idx} className="leading-relaxed">
																	{barrier}
																</li>
															),
														)}
													</ul>
												</div>
											)}
										{groupInsights.aiAnalysis.integratedSynthesis
											.insightToActionGap.supportNeeds &&
											groupInsights.aiAnalysis.integratedSynthesis
												.insightToActionGap.supportNeeds.length > 0 && (
												<div>
													<h6 className="text-sm font-semibold mb-3 text-gray-900">
														Support Needs
													</h6>
													<ul className="list-disc list-inside text-sm text-gray-700 space-y-2.5 pl-2">
														{groupInsights.aiAnalysis.integratedSynthesis.insightToActionGap.supportNeeds.map(
															(need: string, idx: number) => (
																<li key={idx} className="leading-relaxed">
																	{need}
																</li>
															),
														)}
													</ul>
												</div>
											)}
									</CardContent>
								</Card>
							)}

							{/* Topic Manifestation Map */}
							{groupInsights.aiAnalysis.integratedSynthesis
								.topicManifestationMap && (
								<Card className="border-l-4 border-l-purple-500">
									<CardHeader className="pb-3">
										<CardTitle className="text-base flex items-center justify-between">
											<div className="flex items-center space-x-2">
												<BarChart3 className="w-4 h-4" />
												<span>Topic Manifestation Map</span>
											</div>
											<Badge variant="outline" className="text-xs">
												Combined Analysis
											</Badge>
										</CardTitle>
									</CardHeader>
									<CardContent className="space-y-4">
										{groupInsights.aiAnalysis.integratedSynthesis
											.topicManifestationMap.howTopicShowsUp && (
											<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
												<div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
													<h6 className="text-xs font-semibold mb-1 text-blue-900 dark:text-blue-100">
														In Reports
													</h6>
													<p className="text-sm text-blue-800 dark:text-blue-200">
														{
															groupInsights.aiAnalysis.integratedSynthesis
																.topicManifestationMap.howTopicShowsUp.inReports
														}
													</p>
												</div>
												<div className="p-3 bg-orange-50 dark:bg-orange-950 rounded-lg">
													<h6 className="text-xs font-semibold mb-1 text-orange-900 dark:text-orange-100">
														In Check-ins
													</h6>
													<p className="text-sm text-orange-800 dark:text-orange-200">
														{
															groupInsights.aiAnalysis.integratedSynthesis
																.topicManifestationMap.howTopicShowsUp
																.inCheckIns
														}
													</p>
												</div>
											</div>
										)}
										{groupInsights.aiAnalysis.integratedSynthesis
											.topicManifestationMap.hiddenPatterns &&
											groupInsights.aiAnalysis.integratedSynthesis
												.topicManifestationMap.hiddenPatterns.length > 0 && (
												<div>
													<h6 className="text-sm font-semibold mb-2 text-gray-900">
														Hidden Patterns
													</h6>
													<ul className="list-disc list-inside text-sm text-gray-700 space-y-2.5 pl-2">
														{groupInsights.aiAnalysis.integratedSynthesis.topicManifestationMap.hiddenPatterns.map(
															(pattern: string, idx: number) => (
																<li key={idx} className="leading-relaxed">
																	{pattern}
																</li>
															),
														)}
													</ul>
												</div>
											)}
									</CardContent>
								</Card>
							)}
						</div>
					)}

					<div className="text-xs text-muted-foreground pt-3 border-t mt-6">
						<div>
							AI Analysis Generated:{" "}
							{new Date(groupInsights.generatedAt).toLocaleString()}
						</div>
						<div className="mt-1">
							Powered by advanced AI pattern recognition and organizational
							psychology frameworks
						</div>
					</div>
				</>
			)}
		</div>
	);
}
